/**
 * A specialist's coverage claim is separate from whether its code executes. The gateway's specialist returns it
 * with the code (ml-services src/agent/specialist.ts); the tool reports it with the build's result.
 */
import { isToolErrorResult } from './tool-errors';

export interface SpecialistReport {
    status: 'complete' | 'partial' | 'blocked';
    completed: string[];
    remaining: string[];
}

export function readSpecialistReport(value: unknown): SpecialistReport {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Missing specialistReport; return explicit task coverage before executing code.');
    const r = value as Record<string, unknown>;
    // The schema asks for short notes. At runtime, preserve them losslessly
    // within the same 8 × 300 character budget per list: a slightly long note
    // must not discard otherwise usable code or erase reported omissions.
    const list = (v: unknown): v is string[] => Array.isArray(v) && v.length <= 8
        && v.every((s: unknown) => typeof s === 'string' && s.trim().length > 0)
        && v.reduce((total, s: string) => total + s.length, 0) <= 8 * 300;
    if (!['complete', 'partial', 'blocked'].includes(String(r.status)) || !list(r.completed) || !list(r.remaining)) {
        throw new Error('Invalid specialistReport: valid status and completed/remaining lists are required (max 8 nonempty notes and 2400 characters per list).');
    }
    if ((r.status === 'complete' && (r.remaining.length > 0 || r.completed.length === 0))
        || (r.status === 'partial' && (r.remaining.length === 0 || r.completed.length === 0))
        || (r.status === 'blocked' && (r.remaining.length === 0 || r.completed.length > 0))) {
        throw new Error('Contradictory specialistReport: complete needs deliverables and no omissions; partial needs both; blocked needs only blockers. No code executed. Reported coverage and blockers: ' + JSON.stringify(r));
    }
    return { status: r.status as SpecialistReport['status'], completed: r.completed, remaining: r.remaining };
}

/** Direct code/MCP callers may omit the report. Delegation requires it upstream. */
export async function executeWithSpecialistReport(input: Record<string, unknown>, execute: (input: Record<string, unknown>) => Promise<string>): Promise<string> {
    if (input.specialistReport === undefined) return execute(input);
    let report: SpecialistReport;
    try { report = readSpecialistReport(input.specialistReport); }
    catch (error) { return `Error: ${error instanceof Error ? error.message : String(error)}`; }
    if (report.status === 'blocked') return `Error: Specialist blocked; no code executed. ${report.remaining.join('; ')}`;
    const { specialistReport: _report, ...args } = input;
    const result = await execute(args);
    // Keep domain errors first: UI and all provider envelopes use that prefix.
    if (isToolErrorResult(result)) return `${result}\nSpecialist coverage claim (execution failed): ${JSON.stringify(report)}`;
    return `Specialist coverage (self-reported, not independently verified): ${JSON.stringify(report)}\n`
        + (report.status === 'partial' ? 'Task is incomplete. The following execution applied only the reported subset; inspect it and address the remaining work without duplicating it.\n' : '')
        + `Execution result:\n${result}`;
}
